/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Reznak (reznak@cadwork.cz)
 *
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import View from "App/view/view";
import {Framebuffer} from "App/utils/frame_buffer";

import AOBlurShader from "App/render/webgl/shaders/ssao/ao_blur";
import {Containers, Factory} from "App/utils/factory";
import IScene from "js_interface/src/scene_interface";
/**
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {WebGLRenderGraph} aRenderGraph
 * @class
 * @extends Pass
 */
export default 
class AOBlur extends Pass {

    public static TypeName: string = "AOBlur";

    private _quadVBO: WebGLBuffer;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        // VBOs
        this._quadVBO = null;
    }


    /**
     * @param {SceneAPI.Scene} aSceneAPI
     * @param {WebGLRenderGraph} aRenderGraph
     * @return {AOBlur}
     */
    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): AOBlur {
        return new AOBlur(aSceneAPI, aRenderGraph);
    }

    /**
     * Initialize values
     */
    public initialize() {
        if (!this._sceneAPI.lightingModule.getSSAO().isEnabled()) {
            return;
        }

        this.createQuadVBO();
    }

    public release() {
        super.release();

        let gl = this._context.getWebGLRenderingContext();
        gl.deleteBuffer(this._quadVBO);
        this._quadVBO = null;
    };

    /**
     * Create quad VBO
     */
    public createQuadVBO() {
        // Full Screen Quad
        let vertices = [
            // Positions     // Texture Coords
            -1.0, 1.0, 0.0, 0.0, 1.0,
            -1.0, -1.0, 0.0, 0.0, 0.0,
            1.0, 1.0, 0.0, 1.0, 1.0,
            1.0, -1.0, 0.0, 1.0, 0.0
        ];

        let gl = this._context.getWebGLRenderingContext();
        this._quadVBO = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this._quadVBO);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);
    }

    /**
     * Use blur on any greyscale texture.
     * This uses vec4ToFloat and floatToVec4 to reduce memory consumption.
     *
     * @param {View}              aView
     * @param {Framebuffer} aOutput       Unsigned byte, RGBA
     * @param {WebGLTexture}                    aTexture      Unsigned byte, RGBA
     */
    public render(aView: View, aOutput: Framebuffer, aTexture: WebGLTexture) {
        /* jshint camelcase:false */
        this.view = aView;
        this.state.resetRenderNodesFilters();

        let gl = this._context.getWebGLRenderingContext();
        let scale = aOutput.getParameters().scale;
        let position = aView.getPosition();
        let size = aView.getSize();

        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);

        aOutput.bind();
        gl.clear(gl.COLOR_BUFFER_BIT);

        // bind shader
        let shaders = this._renderGraph.getLayer('ssao').getShaders();
        this._activeShader = shaders.get(AOBlurShader.TypeName);
        this._activeShaderRenderable = this._activeShader.get(0);
        this._activeShaderRenderable.render();

        this.state.setRenderablesFilters(this._activeShaderRenderable.getRenderablesFilters());

        // Render quad
        gl.bindBuffer(gl.ARRAY_BUFFER, this._quadVBO);

        // Textures
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, aTexture);
        gl.uniform1i(this._activeShaderRenderable.getUniformLocations().u_texture, 0);

        // Uniforms
        let canvas = this._context.getCanvas();
        let contentSize = canvas.getContentSize();
        gl.uniform2f(this._activeShaderRenderable.getUniformLocations().u_buffer_size, contentSize[0], contentSize[1]);
        gl.uniform4f(this._activeShaderRenderable.getUniformLocations().u_view,
            position[0] * scale / contentSize[0],
            position[1] * scale / contentSize[1],
            size[0] * scale / contentSize[0],
            size[1] * scale / contentSize[1]
        );

        // Attributes
        this._activeShaderRenderable.enableAttributes();
        gl.vertexAttribPointer(this._activeShaderRenderable.getAtttributesLocations().a_position, 3, gl.FLOAT, false, 5 * 4, 0);
        gl.vertexAttribPointer(this._activeShaderRenderable.getAtttributesLocations().a_texcoord, 2, gl.FLOAT, false, 5 * 4, 3 * 4);

        // Draw and release
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
        this._activeShaderRenderable.finishRender();

        // Reset
        this.view = null;
    }
}

Factory.register(Containers.PASSES_SSAO, AOBlur);
